import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, AlertCircle, Infinity, Ban } from 'lucide-react';
import { STATUS_COLORS } from './constants';
import { toast } from 'sonner';
import api from '@/lib/api';

const statusIconMap = { approved: CheckCircle, rejected: XCircle, partially_approved: AlertCircle, cancelled: Ban };

export function RequestItemsTable({ request, canReview, reviewItems, confirmedItems, onUpdateReviewItem, onToggleConfirm, canCancelItems, onItemCancelled }) {
  const { t } = useTranslation();
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelQty, setCancelQty] = useState(1);
  const [cancelling, setCancelling] = useState(false);

  const openCancelDialog = (item) => {
    const cancellable = (item.approved_quantity || 0) - (item.cancelled_quantity || 0);
    setCancelDialog(item);
    setCancelQty(cancellable);
  };

  const handleItemCancel = async () => {
    if (!cancelDialog) return;
    setCancelling(true);
    try {
      const res = await api.put(`/api/requests/${request.id}/items/${cancelDialog.id}/cancel`, { cancel_quantity: cancelQty });
      toast.success(res.data.message);
      setCancelDialog(null);
      if (onItemCancelled) onItemCancelled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    } finally { setCancelling(false); }
  };

  return (
    <>
      <div className="border-t border-gray-100">
        <div className="px-5 py-3 bg-gray-50/50">
          <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">{t('request_items')}</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-gray-100 hover:bg-transparent">
              <TableHead className="text-xs text-gray-400 font-medium text-center">{t('right')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium text-center">{t('requested')}</TableHead>
              {canReview && <TableHead className="text-xs text-gray-400 font-medium text-center">{t('approved_qty')}</TableHead>}
              {canReview && <TableHead className="text-xs text-gray-400 font-medium text-center">{t('rejected')}</TableHead>}
              {!canReview && <TableHead className="text-xs text-gray-400 font-medium text-center">{t('approved')}</TableHead>}
              {!canReview && <TableHead className="text-xs text-gray-400 font-medium text-center">{t('rejected')}</TableHead>}
              {!canReview && <TableHead className="text-xs text-gray-400 font-medium text-center">Cancelled</TableHead>}
              <TableHead className="text-xs text-gray-400 font-medium text-center">{t('status')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium text-center hidden md:table-cell">{t('balance_info')}</TableHead>
              {canReview && <TableHead className="text-xs text-gray-400 font-medium text-center w-12"></TableHead>}
              {canCancelItems && !canReview && <TableHead className="text-xs text-gray-400 font-medium text-center w-20"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(request.items || []).map((item, idx) => {
              const Icon = statusIconMap[item.item_status];
              const cancellable = (item.approved_quantity || 0) - (item.cancelled_quantity || 0);
              const isItemCancellable = canCancelItems && !canReview && item.item_status !== 'cancelled' && item.item_status !== 'rejected' && cancellable > 0;
              return (
                <TableRow key={item.id} className={`border-gray-100 transition-all ${canReview && confirmedItems[idx] ? 'opacity-60 bg-green-50/30' : ''} ${item.item_status === 'cancelled' ? 'opacity-50' : ''}`}>
                  <TableCell className="text-center">
                    <p className="font-medium text-gray-800 text-sm">{item.right_name}</p>
                    <p className="text-xs text-gray-400 capitalize">{item.unit_type}</p>
                  </TableCell>
                  <TableCell className="font-medium text-gray-800 text-sm text-center">{item.requested_quantity}</TableCell>
                  {canReview && (
                    <TableCell className="text-center">
                      <Input data-testid={`approve-qty-${idx}`} type="number" min="0" max={item.requested_quantity}
                        value={reviewItems[idx]?.approved_quantity ?? ''}
                        onChange={e => onUpdateReviewItem(idx, 'approved_quantity', parseInt(e.target.value) || 0)}
                        disabled={confirmedItems[idx]}
                        className={`w-20 border-gray-200 h-8 text-sm text-center mx-auto ${confirmedItems[idx] ? 'bg-green-50 opacity-70 cursor-not-allowed' : 'bg-gray-50'}`} />
                    </TableCell>
                  )}
                  {canReview && (
                    <TableCell className="text-red-500 font-medium text-sm text-center">
                      {item.requested_quantity - (reviewItems[idx]?.approved_quantity || 0)}
                    </TableCell>
                  )}
                  {!canReview && (
                    <TableCell className="text-green-600 font-medium text-sm text-center">
                      {item.cancelled_quantity > 0
                        ? `${(item.approved_quantity || 0) - (item.cancelled_quantity || 0)}`
                        : (item.approved_quantity ?? '---')}
                    </TableCell>
                  )}
                  {!canReview && <TableCell className="text-red-500 font-medium text-sm text-center">{item.rejected_quantity ?? '---'}</TableCell>}
                  {!canReview && (
                    <TableCell className="text-amber-500 font-medium text-sm text-center">
                      {item.cancelled_quantity > 0 ? item.cancelled_quantity : '---'}
                    </TableCell>
                  )}
                  <TableCell className="text-center">
                    <Badge className={`text-xs font-normal pointer-events-none select-none ${STATUS_COLORS[item.item_status] || 'bg-gray-100 text-gray-500'}`}>
                      {Icon && <Icon size={11} className="mr-1" />}
                      {item.item_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-center">
                    {item.allocation_type === 'unlimited' ? (
                      <span className="flex items-center justify-center gap-1 text-emerald-500 text-xs"><Infinity size={12} /> {t('unlimited')}</span>
                    ) : (
                      <div className="text-xs text-gray-400 space-y-0.5">
                        <div>{t('remain')}: <span className="font-medium text-gray-600">{item.remaining_amount}</span></div>
                        <div>{t('pending')}: <span className="font-medium text-gray-600">{item.pending_amount}</span></div>
                        {item.per_match_limit && <div>{t('per_match_max')}: <span className="font-medium text-gray-600">{item.per_match_limit}</span></div>}
                      </div>
                    )}
                  </TableCell>
                  {canReview && (
                    <TableCell className="text-center">
                      <button data-testid={`toggle-item-${idx}`} onClick={() => onToggleConfirm(idx)}
                        className={`p-1.5 rounded-lg transition-all ${confirmedItems[idx] ? 'bg-green-100 text-green-700 ring-1 ring-green-300 shadow-sm' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                        title={confirmedItems[idx] ? t('confirmed') || 'Confirmed' : t('click_to_confirm') || 'Click to confirm'}>
                        <CheckCircle size={16} />
                      </button>
                    </TableCell>
                  )}
                  {canCancelItems && !canReview && (
                    <TableCell className="text-center">
                      {isItemCancellable && (
                        <Button variant="ghost" size="sm" data-testid={`cancel-item-${item.id}`}
                          onClick={() => openCancelDialog(item)}
                          className="h-7 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950">
                          <Ban size={12} className="mr-1" /> Cancel
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Item Cancel Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="bg-white dark:bg-gray-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg">Cancel: {cancelDialog?.right_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>Approved: <strong className="text-gray-800 dark:text-gray-200">{cancelDialog?.approved_quantity}</strong></span>
              <span>Already cancelled: <strong className="text-amber-500">{cancelDialog?.cancelled_quantity || 0}</strong></span>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Quantity to cancel</label>
              <Input data-testid="cancel-item-qty-input" type="number" min={1}
                max={(cancelDialog?.approved_quantity || 0) - (cancelDialog?.cancelled_quantity || 0)}
                value={cancelQty} onChange={e => setCancelQty(parseInt(e.target.value) || 0)}
                className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 h-9 text-sm" />
              <p className="text-xs text-gray-400 mt-1">
                Max: {(cancelDialog?.approved_quantity || 0) - (cancelDialog?.cancelled_quantity || 0)}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCancelDialog(null)} className="flex-1 text-sm h-9">Keep</Button>
              <Button onClick={handleItemCancel} disabled={cancelling || cancelQty <= 0}
                className="flex-1 text-sm h-9 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-item-btn">
                {cancelling ? '...' : `Cancel ${cancelQty}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
